import {
  CHART_ALL_FEEDBACK_ALIAS,
  CHART_FIELD,
  CHART_ROW_TOTAL_COLUMN_KEY,
  CHART_TOTAL_ROW_KEY,
} from '@/entities/chart/model/constants';
import type { SeriesTotals } from '@/features/(charts)/chart-renderer/ui/Cross/types';

import {
  CellValueCalculator,
  SentimentCalculator,
  TotalCalculator,
} from '../calculators';
import { DataSourceGenerator } from './DataSourceGenerator';

/**
 * 다중 시리즈 데이터 소스 생성기
 * 각 시리즈별로 독립적인 데이터를 생성
 */
export class MultiSeriesDataGenerator extends DataSourceGenerator {
  /**
   * 다중 시리즈 데이터 소스 생성
   * - 각 시리즈별로 독립적인 total 계산
   * - 각 시리즈별로 데이터 생성
   * - showRowTotal에 따라 시리즈별 Row Total Column 데이터 추가
   * - showColumnTotal이 true인 시리즈의 Grand Total Row 추가
   */
  buildDataSource(): Record<string, StringNumberValue | null>[] {
    const { chart, configs, crossTableData, sortedCandidates, helpers } =
      this.params;
    const crossTable = crossTableData;

    // 각 시리즈별로 total 계산
    const seriesTotals: (SeriesTotals | null)[] = chart.series.map(
      (series, seriesIndex) => {
        const seriesConfig = configs[seriesIndex];
        if (!seriesConfig || !series.cross_field) return null;

        const yHeaders =
          series.cross_field.type === CHART_FIELD.FEEDBACK_ALL
            ? [CHART_ALL_FEEDBACK_ALIAS]
            : series.cross_field.candidates || [];
        const seriesTableData = crossTable.tables[seriesIndex] || [];

        const totals = this.calculateTotals(
          seriesTableData,
          yHeaders,
          sortedCandidates,
          series.cross_field.type === CHART_FIELD.FEEDBACK_ALL,
        );

        return {
          ...totals,
          yHeaders,
          seriesTableData,
        };
      },
    );

    const result: Record<string, StringNumberValue | null>[] =
      sortedCandidates?.map((item) => {
        const fields: Record<string, number | null> = {};

        // 각 시리즈별로 데이터 처리
        chart.series.forEach((series, seriesIndex) => {
          const seriesConfig = configs[seriesIndex];
          if (!seriesConfig || !series.cross_field) return;

          const seriesTableData = crossTable.tables[seriesIndex] || [];
          const currentData = seriesTableData.find(
            (d: ChartTableXValue) => d.x === item,
          );

          const totals = seriesTotals[seriesIndex];
          if (!totals) return;

          // 각 cross_field candidate별로 데이터 설정
          let rowTotal = 0;
          if (series.cross_field?.type === CHART_FIELD.FEEDBACK_ALL) {
            const columnKey = `series-${seriesIndex}-${CHART_ALL_FEEDBACK_ALIAS}`;
            const yData = currentData?.values[0];
            const value = yData?.value || 0;

            // FEEDBACK_ALL의 경우에도 rowTotal에 값 추가
            rowTotal += value;

            if (seriesConfig.isPercentage) {
              const totalForPercentage = TotalCalculator.getTotalForPercentage(
                seriesConfig.shareCalculationType,
                item,
                CHART_ALL_FEEDBACK_ALIAS,
                totals,
              );
              fields[columnKey] = CellValueCalculator.calculate(
                seriesConfig,
                value,
                totalForPercentage,
                yData?.values,
              );
            } else {
              fields[columnKey] = value;
            }
          } else {
            (series.cross_field.candidates || []).forEach((alias) => {
              const yData = currentData?.values?.find((y) => y.y === alias);
              const value = yData?.value || 0;
              rowTotal += value;

              const columnKey = `series-${seriesIndex}-${alias}`;

              // 퍼센트 계산이 필요한 경우 처리
              if (seriesConfig.isPercentage) {
                const totalForPercentage =
                  TotalCalculator.getTotalForPercentage(
                    seriesConfig.shareCalculationType,
                    item,
                    alias,
                    totals,
                  );
                fields[columnKey] = CellValueCalculator.calculate(
                  seriesConfig,
                  value,
                  totalForPercentage,
                  yData?.values,
                );
              } else {
                fields[columnKey] = value;
              }
            });
          }

          // 시리즈별 Row Total Column 추가 (show_row_total이 true인 경우)
          if (seriesConfig.showRowTotal) {
            const rowTotalColumnKey = `series-${seriesIndex}-${CHART_ROW_TOTAL_COLUMN_KEY}`;
            fields[rowTotalColumnKey] = TotalCalculator.calculateRowTotalValue(
              seriesConfig,
              rowTotal,
              totals.grandTotal,
            );
          }
        });

        const levelRowMap = helpers.getLevelRowMap(item);
        return {
          key: item,
          ...levelRowMap,
          ...fields,
        };
      }) || [];

    // Grand Total Row 추가 (하단 합계 행 - showColumnTotal이 true인 시리즈가 하나라도 있으면 표시)
    const hasAnyColumnTotal = configs.some((config) => config?.showColumnTotal);
    if (hasAnyColumnTotal) {
      const totalRowFields: Record<string, number | null> = {};

      chart.series.forEach((series, seriesIndex) => {
        const seriesConfig = configs[seriesIndex];
        if (!seriesConfig || !series.cross_field) return;

        const totals = seriesTotals[seriesIndex];

        if (!totals) return;

        // Grand Total Row의 각 컬럼 값 계산
        if (seriesConfig.showColumnTotal) {
          if (series.cross_field?.type === CHART_FIELD.FEEDBACK_ALL) {
            const columnKey = `series-${seriesIndex}-${CHART_ALL_FEEDBACK_ALIAS}`;
            if (seriesConfig.isCellSentimentCalculation) {
              totalRowFields[columnKey] = Number(
                SentimentCalculator.calculateTotalSentimentPercentage(
                  CHART_ALL_FEEDBACK_ALIAS,
                  totals.seriesTableData,
                  seriesConfig.sentimentSegment,
                ).toFixed(2),
              );
            } else {
              totalRowFields[columnKey] =
                TotalCalculator.calculateColumnTotalValue(
                  seriesConfig,
                  totals.yTotalMap[CHART_ALL_FEEDBACK_ALIAS],
                  totals.grandTotal,
                );
            }
          } else {
            (series.cross_field.candidates || []).forEach((alias) => {
              // 시리즈별 컬럼 키 생성
              const columnKey = `series-${seriesIndex}-${alias}`;

              if (seriesConfig.isCellSentimentCalculation) {
                totalRowFields[columnKey] = Number(
                  SentimentCalculator.calculateTotalSentimentPercentage(
                    alias,
                    totals.seriesTableData,
                    seriesConfig.sentimentSegment,
                  ).toFixed(2),
                );
              } else {
                totalRowFields[columnKey] =
                  TotalCalculator.calculateColumnTotalValue(
                    seriesConfig,
                    totals.yTotalMap[alias],
                    totals.grandTotal,
                  );
              }
            });
          }

          // 시리즈별 Row Total Column의 Grand Total 값 (show_row_total이 true인 경우만)
          if (seriesConfig.showRowTotal) {
            const rowTotalColumnKey = `series-${seriesIndex}-${CHART_ROW_TOTAL_COLUMN_KEY}`;
            totalRowFields[rowTotalColumnKey] =
              TotalCalculator.calculateGrandTotalValue(
                seriesConfig,
                totals.grandTotal,
              );
          }
        } else {
          // showColumnTotal이 false인 경우, null로 설정하여 '-' 표시
          if (series.cross_field?.type === CHART_FIELD.FEEDBACK_ALL) {
            const columnKey = `series-${seriesIndex}-${CHART_ALL_FEEDBACK_ALIAS}`;
            totalRowFields[columnKey] = null;
          } else {
            (series.cross_field.candidates || []).forEach((alias) => {
              const columnKey = `series-${seriesIndex}-${alias}`;
              totalRowFields[columnKey] = null;
            });
          }

          // Row Total Column도 null 처리
          if (seriesConfig.showRowTotal) {
            const rowTotalColumnKey = `series-${seriesIndex}-${CHART_ROW_TOTAL_COLUMN_KEY}`;
            totalRowFields[rowTotalColumnKey] = null;
          }
        }
      });

      result.push({
        key: CHART_TOTAL_ROW_KEY,
        ...helpers.getTotalRowMap(),
        ...totalRowFields,
      });
    }

    return result;
  }
}
